import { EventSearchFilter } from '../models/event-filter';
import { EventFilterType } from '../models/event-filter-type-enum';
import { DateUtil } from '../../shared/utils/date.util';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UISearchStatusMappingEnum } from '../models/ui-search-status-mapping-enum';
import { OrderedSet } from 'immutable';
import { EventMenuSearchFilter } from '../models/event-menu-search-filter';
import { UISearchTypeMappingEnum } from '../models/ui-search-type-mapping-enum';

@Injectable()
export class EventFilterUtilsService {
  static readonly dateRangeValueSeparator = ';';

  /**
   * Parses 2 dates represented as strings and returns the value to store in the EventFilterType.DATE_RANGE filter
   * @param date1
   * @param date2
   * @returns {string}
   */
  static getDateRangeFilterValue(date1: string, date2: string): string {
    return DateUtil.formatDate(date1) + this.dateRangeValueSeparator + DateUtil.formatDate(date2);
  }

  static parseFilterValueAsDateRange(dateRangeString: string): string[] {
    return dateRangeString.split(this.dateRangeValueSeparator);
  }

  constructor(private translateService: TranslateService) {
  }

  /**
   * This method returns a key to identify what kind of token to make.
   * This is useful to ensure that two filters with the same key can't be present at the same time.
   * @param filter
   * @returns string
   */
  getUniqueTokenKey(filter: EventSearchFilter): string {
    // only 1 date filter is allowed at a time
    if (filter.filterField === EventFilterType.DATE_RANGE_PROPERTY.toString() ||
      filter.filterField === EventFilterType.DATE_BEFORE_PROPERTY.toString() ||
      filter.filterField === EventFilterType.DATE_AFTER_PROPERTY.toString()) {
      return EventFilterType.DATE_RANGE_PROPERTY.toString();
    } else if (filter.filterField === EventFilterType.DATE_RANGE_AGENCY.toString() ||
      filter.filterField === EventFilterType.DATE_BEFORE_AGENCY.toString() ||
      filter.filterField === EventFilterType.DATE_AFTER_AGENCY.toString()) {
      return EventFilterType.DATE_RANGE_AGENCY.toString();
    } else if (filter.filterField === EventFilterType.GUEST_NAME.toString() ||
      filter.filterField === EventFilterType.PAYMENT_STATUS.toString() ||
      filter.filterField === EventFilterType.EVENT_PAYMENT_STATUS.toString()) {
      // these filters allow for for multiple tokens
      return filter.filterField + ':' + filter.filterValue;
    }
    // by default, there can only be 1 instance of a filter token at a time
    return filter.filterField;
  }

  /**
   * Returns a string representation of a given Date that will be displayed in a search filter token
   * @param date
   * @returns {string}
   */
  getDateStringForToken(date: Date): string {
    return (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
  }

  /**
   * This method will return the string used to display the value of the filter in a token
   * @param filter
   * @returns {string}
   */
  getTokenDisplayString(filter: EventSearchFilter): string {
    if (filter.filterField === EventFilterType.COMPLETED.toString()) {
      return this.translateService.instant('EVENTS.EVENT_STATUS_COMPLETED');
    } else if (filter.filterField === EventFilterType.MISSING_AGENCY.toString()) {
      return this.translateService.instant('EVENTS.EVENT_STATUS_MISSING_AGENCY');
    } else if (filter.filterField === EventFilterType.ARCHIVED.toString()) {
      return this.translateService.instant('EVENTS.EVENT_STATUS_ARCHIVED');
    } else if (filter.filterField === EventFilterType.DELETED.toString()) {
      return this.translateService.instant('EVENTS.EVENT_STATUS_DELETED');
    } else if (filter.filterField === EventFilterType.COMM_RETENTION.toString()) {
      return this.translateService.instant('EVENTS.EVENT_STATUS_COMM_RETENTION');
    } else if (filter.filterField === EventFilterType.DATE_RANGE_PROPERTY.toString()
            || filter.filterField === EventFilterType.DATE_RANGE_AGENCY.toString()) {
      const strings = filter.filterValue.split(EventFilterUtilsService.dateRangeValueSeparator);
      if (strings.length === 2) {
        return this.getDateStringForToken(DateUtil.parseDate(strings[0])) + ' - ' +
          this.getDateStringForToken(DateUtil.parseDate(strings[1]));
      } else {
        return 'error';
      }
    } else if (filter.filterField === EventFilterType.DATE_BEFORE_PROPERTY.toString()
            || filter.filterField === EventFilterType.DATE_AFTER_PROPERTY.toString()
            || filter.filterField === EventFilterType.DATE_BEFORE_AGENCY.toString()
            || filter.filterField === EventFilterType.DATE_AFTER_AGENCY.toString()) {
      return this.getDateStringForToken(DateUtil.parseDate(filter.filterValue));
    } else if (filter.filterField === EventFilterType.PAYMENT_STATUS.toString()) {
      return this.labelForPaymentStatusValue(filter.filterValue);
    } else if (filter.filterField === EventFilterType.EVENT_PAYMENT_STATUS.toString()) {
      return this.labelForEventPaymentStatusValue(filter.filterValue);
    } else if (filter.filterField === EventFilterType.PAYMENT_TYPE.toString()) {
      return this.labelForEventTypeValue(filter.filterValue);
    } else if (filter.filterField === EventFilterType.MISSING_EMAIL_CONTACT.toString()) {
      return this.labelForPaymentStatusValue(filter.filterValue);
    } else {
      return filter.filterValue;
    }
  }

  labelForEventPaymentStatusValue(filterValue: string): string {
    let label = filterValue;

    if (filterValue === UISearchStatusMappingEnum.PAID.toString()) {
      label = 'EVENTS.EVENT_PAYMENT_STATUS_FILTER_VALUE.PAID';
    } else if (filterValue === UISearchStatusMappingEnum.PARTIAL.toString()) {
      label = 'EVENTS.EVENT_PAYMENT_STATUS_FILTER_VALUE.PARTIAL';
    } else if (filterValue === UISearchStatusMappingEnum.UNPAID.toString()) {
      label = 'EVENTS.EVENT_PAYMENT_STATUS_FILTER_VALUE.UNPAID';
    }

    return this.translateService.instant(label);
  }

  labelForPaymentStatusValue(filterValue: string): string {
    let label = filterValue;

    if (filterValue === UISearchStatusMappingEnum.PAID.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PAID';
    } else if (filterValue === UISearchStatusMappingEnum.UNPAID.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.UNPAID';
    } else if (filterValue === UISearchStatusMappingEnum.PAYMENT_IN_PROCESS.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PAYMENT_IN_PROCESS';
    } else if (filterValue === UISearchStatusMappingEnum.CANCELLED.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.CANCELLED';
    } else if (filterValue === UISearchStatusMappingEnum.INVOICED.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.INVOICED';
    } else if (filterValue === UISearchStatusMappingEnum.PAID_DIRECT.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PAID_DIRECT';
    } else if (filterValue === UISearchStatusMappingEnum.PAY_SENT.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PAY_SENT';
    } else if (filterValue === UISearchStatusMappingEnum.PENDING.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PENDING';
    } else if (filterValue === UISearchStatusMappingEnum.READY_TO_INITIATE.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.READY_TO_INITIATE';
    } else if (filterValue === UISearchStatusMappingEnum.REQUEST_REJECTED.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.REQUEST_REJECTED';
    } else if (filterValue === UISearchStatusMappingEnum.REQUESTED.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.REQUESTED';
    } else if (filterValue === UISearchStatusMappingEnum.RESENT_REQUEST.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.RESENT_REQUEST';
    } else if (filterValue === UISearchStatusMappingEnum.FUNDED.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.FUNDED';
    } else if (filterValue === UISearchStatusMappingEnum.PAY_CLEARED.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PAY_CLEARED';
    } else if (filterValue === UISearchStatusMappingEnum.CHECK_STOPPED.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.CHECK_STOPPED';
    } else if (filterValue === UISearchStatusMappingEnum.PAY_REISSUED.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PAY_REISSUED';
    } else if (filterValue === UISearchStatusMappingEnum.REJECTED_PAYMENT_REQUESTS.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.REJECTED_PAYMENT_REQUESTS';
    } else if (filterValue === UISearchStatusMappingEnum.REQUEST_PAYMENTS.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.REQUEST_PAYMENTS';
    } else if (filterValue === UISearchStatusMappingEnum.MISSING_PAYMENT_ITEMS.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.MISSING_PAYMENT_ITEMS';
    } else if (filterValue === UISearchStatusMappingEnum.MISSING_CONTACT_EMAIL.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.MISSING_CONTACT_EMAIL';
    }

    return this.translateService.instant(label);
  }

  labelForEventTypeValue(filterValue: string): string {
    let label = filterValue;

    if (filterValue === UISearchTypeMappingEnum.HAS_PREPAYMENT.toString()) {
      label = 'EVENTS.EVENT_TYPE_FILTER_VALUE.HAS_PREPAYMENT';
    } else if (filterValue === UISearchTypeMappingEnum.FINAL.toString()) {
      label = 'EVENTS.EVENT_TYPE_FILTER_VALUE.FINAL';
    }

    return this.translateService.instant(label);
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/event-filter-util-service.ts